import React, { useState, useMemo, ReactNode } from "react";
import styled from "@emotion/styled";
import { css, Theme } from "@emotion/react";
import Link from "next/link";
import Image from "next/image";
import {
  Currency,
  ImpactShopProductType,
  ImpactType,
  GLOBAL_TREES_SLUG,
  UK_TREES_SLUG,
  WILDFLOWER_SLUG,
  PEATLANDS_SLUG,
  SEAGRASS_SLUG,
  penceToDisplayPrice,
} from "@ecologi/shared";
import CarbonIcon from "@ecologi/assets/icons/UnsureWhatToFund/carbon.svg";
import TreeIcon from "@ecologi/assets/icons/UnsureWhatToFund/tree.svg";
import FlowerIcon from "@ecologi/assets/icons/UnsureWhatToFund/flower.svg";
import HelpIcon from "@ecologi/assets/icons/UnsureWhatToFund/help.svg";
import CalculatorIcon from "@ecologi/assets/icons/UnsureWhatToFund/calculator.svg";
import PoundIcon from "@ecologi/assets/icons/UnsureWhatToFund/pound.svg";
import RecurringIcon from "@ecologi/assets/icons/UnsureWhatToFund/recurring.svg";
import LocalIcon from "@ecologi/assets/icons/UnsureWhatToFund/local.svg";
import GlobeIcon from "@ecologi/assets/icons/UnsureWhatToFund/globe.svg";
import LandIcon from "@ecologi/assets/icons/UnsureWhatToFund/land.svg";
import SeaIcon from "@ecologi/assets/icons/UnsureWhatToFund/sea.svg";
import HeadsetIcon from "@ecologi/assets/icons/UnsureWhatToFund/headset.svg";
import { DialogModal } from "../DialogModal";
import { Heading } from "../Heading";
import { Typography } from "../Typography";

const interactiveCardStyles = ({ theme }: { theme: Theme }) => css`
  background-color: ${theme.colors.jungleLighter};
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.jungleLight};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${theme.colors.forest};
  }
`;

const IconWrapper = styled.div<{ size?: number; svgSize?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ size = 48 }) => size}px;
  height: ${({ size = 48 }) => size}px;
  color: ${({ theme }) => theme.colors.forest};

  svg {
    width: ${({ svgSize, size = 48 }) => svgSize ?? size * 0.83}px;
    height: ${({ svgSize, size = 48 }) => svgSize ?? size * 0.83}px;
  }
`;

const PillButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.colors.forest};
  color: ${({ theme }) => theme.colors.snowdrop};
  border-radius: 100px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.forestDark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

type QuestionOption = {
  label: string;
  next: string;
  icon?: ReactNode;
  description?: string;
  dynamicPrice?: "uk-trees" | "global-trees" | "land-habitat" | "sea-habitat";
};

type QuestionNode = {
  id: string;
  question: string;
  options: QuestionOption[];
};

const questionTree: Record<string, QuestionNode> = {
  goal: {
    id: "goal",
    question: "What's your goal?",
    options: [
      {
        label: "Offset your emissions",
        next: "emission-type",
        icon: <CarbonIcon />,
      },
      {
        label: "Fund tree planting",
        next: "tree-budget",
        icon: <TreeIcon />,
      },
      {
        label: "Fund habitat restoration",
        next: "habitat-budget",
        icon: <FlowerIcon />,
      },
    ],
  },
  "tree-budget": {
    id: "tree-budget",
    question: "Fund tree planting",
    options: [
      {
        label: "I have a specific budget",
        next: "tree-location",
        icon: <PoundIcon />,
      },
      {
        label: "I want to plant a specific amount of trees",
        next: "tree-calculation",
        icon: <TreeIcon />,
      },
    ],
  },
  "tree-calculation": {
    id: "tree-calculation",
    question: "How often do you want to fund trees?",
    options: [
      {
        label: "Just once",
        next: "tree-location",
        icon: <PoundIcon />,
        description: "Fund a one-off purchase for a special occasion",
      },
      {
        label: "On a recurring basis",
        next: "fixed-amount-per",
        icon: <RecurringIcon />,
        description: "Multiply your impact by funding on a recurring basis",
      },
    ],
  },
  "fixed-amount-per": {
    id: "fixed-amount-per",
    question: "Recurring trees would be funded...",
    options: [
      { label: "Per client", next: "tree-location" },
      { label: "Per project", next: "tree-location" },
      { label: "Per product", next: "tree-location" },
      { label: "Per something else", next: "tree-location" },
    ],
  },
  "tree-location": {
    id: "tree-location",
    question: "Where do you want to fund trees?",
    options: [
      {
        label: "I prefer a local tree",
        next: "uk-trees",
        icon: <LocalIcon />,
        dynamicPrice: "uk-trees",
        description:
          "Keeps climate action close to home, benefiting nearby ecosystems and communities.",
      },
      {
        label: "I prefer a global tree",
        next: "global-trees",
        icon: <GlobeIcon />,
        dynamicPrice: "global-trees",
        description:
          "Global tree projects can deliver stronger community impact, supporting livelihoods and long-term resilience.",
      },
    ],
  },
  "emission-type": {
    id: "emission-type",
    question: "I want to offset...",
    options: [
      { label: "Travel emissions", next: "measured" },
      { label: "Utility consumption emissions", next: "measured" },
      { label: "Product emissions", next: "measured" },
      { label: "Scope 1, 2 or 3 emissions", next: "measured" },
      { label: "Delivery emissions", next: "measured" },
      { label: "Other", next: "measured" },
    ],
  },
  measured: {
    id: "measured",
    question: "Have you measured your emissions?",
    options: [
      {
        label: "I've measured my emissions",
        next: "carbon-projects",
        icon: <CalculatorIcon />,
        description: "I need help to measure my emissions",
      },
      {
        label: "I haven't measured my emissions",
        next: "schedule-call",
        icon: <HelpIcon />,
        description: "Utility consumption emissions",
      },
    ],
  },
  "habitat-budget": {
    id: "habitat-budget",
    question: "Fund habitat restoration",
    options: [
      {
        label: "I have a specific budget",
        next: "habitat-preference",
        icon: <PoundIcon />,
      },
      {
        label: "I want to plant a specific amount of trees",
        next: "habitat-calculation",
        icon: <TreeIcon />,
      },
    ],
  },
  "habitat-calculation": {
    id: "habitat-calculation",
    question: "How often do you want to fund habitat restoration?",
    options: [
      {
        label: "Just once",
        next: "habitat-preference",
        icon: <PoundIcon />,
        description: "Fund a one-off purchase for a special occasion",
      },
      {
        label: "On a recurring basis",
        next: "habitat-fixed-amount-per",
        icon: <RecurringIcon />,
        description: "Multiply your impact by funding on a recurring basis",
      },
    ],
  },
  "habitat-fixed-amount-per": {
    id: "habitat-fixed-amount-per",
    question: "Recurring habitat would be funded...",
    options: [
      { label: "Per client", next: "habitat-preference" },
      { label: "Per project", next: "habitat-preference" },
      { label: "Per product", next: "habitat-preference" },
      { label: "Per something else", next: "habitat-preference" },
    ],
  },
  "habitat-preference": {
    id: "habitat-preference",
    question: "Fund land or sea habitat",
    options: [
      {
        label: "I prefer funding land habitat",
        next: "land-habitat-products",
        icon: <LandIcon />,
        dynamicPrice: "land-habitat",
        description:
          "Keeps climate action close to home, benefiting nearby ecosystems and communities.",
      },
      {
        label: "I prefer funding sea habitat",
        next: "seagrass",
        icon: <SeaIcon />,
        dynamicPrice: "sea-habitat",
        description:
          "Global tree projects can deliver stronger community impact, supporting livelihoods and long-term resilience.",
      },
    ],
  },
};

const INITIAL_STEP = "goal";

const TERMINAL_NODES: Record<string, string> = {
  "carbon-projects": "Carbon credit projects",
  "schedule-call": "Speak to the team",
  "uk-trees": "UK tree planting",
  "global-trees": "Global tree planting",
  "land-habitat-products": "Land habitat restoration",
  seagrass: "Sea habitat restoration",
};

const isTerminalNode = (step: string): boolean => step in TERMINAL_NODES;

const MAX_QUESTION_PATH_LENGTH = 2000;

type HistoryEntry = {
  step: string;
  answer: string;
};

type QuestionPathResult = {
  questionPath: string;
  questionPathTruncatedAtStart: boolean;
};

const formatQuestionPath = (
  historyEntries: HistoryEntry[],
  currentStep?: string,
  currentAnswer?: string
): QuestionPathResult => {
  const entries = [...historyEntries];

  if (currentStep && currentAnswer) {
    entries.push({ step: currentStep, answer: currentAnswer });
  }

  let pathObject: Record<string, string> = {};
  entries.forEach((entry) => {
    pathObject[entry.step] = entry.answer;
  });

  let result = JSON.stringify(pathObject);
  let truncated = false;

  while (result.length > MAX_QUESTION_PATH_LENGTH && entries.length > 1) {
    entries.shift();
    pathObject = {};
    entries.forEach((entry) => {
      pathObject[entry.step] = entry.answer;
    });
    result = JSON.stringify(pathObject);
    truncated = true;
  }

  return {
    questionPath: result,
    questionPathTruncatedAtStart: truncated,
  };
};

const generateSessionId = (): string => {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const QuestionHeading = styled(Heading)`
  text-align: left;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.forest};
`;

const GoalCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media ${({ theme }) => theme.bp.tabletDown} {
    grid-template-columns: 1fr;
  }
`;

const GoalCard = styled.button`
  all: unset;
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  min-height: 200px;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.forest};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
`;

const GoalCardBackground = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.forest};
`;

const GoalCardContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 16px;
  color: white;
`;

const GoalCardIcon = styled(IconWrapper)`
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
  color: white;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const GoalCardTitle = styled(Typography)`
  font-weight: 600;
  font-size: 18px;
  color: white;
`;

const OptionsContainer = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${({ columns }) =>
    columns === 3 ? "repeat(3, 1fr)" : "repeat(2, 1fr)"};
  gap: 16px;

  @media ${({ theme }) => theme.bp.tabletDown} {
    grid-template-columns: 1fr;
  }
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioOption = styled.button`
  all: unset;
  ${interactiveCardStyles}
  padding: 16px 20px;
  border-radius: 8px;
  text-align: left;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.forest};
`;

const OptionCard = styled.button`
  all: unset;
  ${interactiveCardStyles}
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  text-align: left;
`;

const HorizontalOptionCard = styled.button`
  all: unset;
  ${interactiveCardStyles}
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  text-align: left;
`;

const OptionIconWrapper = styled(IconWrapper)`
  margin-bottom: 16px;
`;

const OptionTitle = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.forest};
`;

const OptionPrice = styled(Typography)`
  color: ${({ theme }) => theme.colors.forest};
  font-size: 14px;
  margin-top: 4px;
`;

const OptionDescription = styled(Typography)`
  color: ${({ theme }) => theme.colors.forest};
  font-size: 14px;
  line-height: 1.4;
  margin-top: 12px;
`;

const BackButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.charcoal};
  padding: 8px;
  border-radius: 6px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    background: ${({ theme }) => theme.colors.mist};
  }
`;

const ProjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media ${({ theme }) => theme.bp.tabletUp} {
    max-height: 400px;
    flex: initial;
  }
`;

const ProjectCard = styled.a`
  ${interactiveCardStyles}
  display: flex;
  text-decoration: none;
`;

const ProjectImageWrapper = styled.div`
  flex-shrink: 0;
  width: 180px;
  position: relative;
  overflow: hidden;
  border-radius: 12px 0 0 12px;

  @media ${({ theme }) => theme.bp.mobileDown} {
    width: 120px;
  }
`;

const ProjectDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 24px;
  flex: 1;
`;

const ProjectTitle = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.forest};
  margin-bottom: 6px;
`;

const ProjectSnippet = styled(Typography)`
  color: ${({ theme }) => theme.colors.forest};
  font-size: 14px;
  margin-bottom: 12px;
`;

const ProjectPrice = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.forest};
  font-size: 18px;
`;

const SeeOtherLink = styled.a`
  display: block;
  text-align: center;
  color: ${({ theme }) => theme.colors.forest};
  text-decoration: underline;
  margin-top: 16px;
  font-size: 14px;

  &:hover {
    opacity: 0.8;
  }
`;

const ScheduleCallCard = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.jungleLighter};
  border-radius: 12px;

  @media ${({ theme }) => theme.bp.mobileDown} {
    flex-direction: column;
    text-align: center;
  }
`;

const ScheduleCallContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ScheduleCallTitle = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.forest};
`;

const ScheduleCallText = styled(Typography)`
  color: ${({ theme }) => theme.colors.forest};
`;

export type UnsureWhatToFundModalProps = {
  isOpen: boolean;
  onClose: () => void;
  products: ImpactShopProductType[];
  currency: Currency;
  onAnalyticsEvent?: (
    eventName: string,
    props: Record<string, string | number | boolean>
  ) => void;
};

export const UnsureWhatToFundModal = ({
  isOpen,
  onClose,
  products,
  currency,
  onAnalyticsEvent,
}: UnsureWhatToFundModalProps) => {
  const [currentStep, setCurrentStep] = useState<string>(INITIAL_STEP);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [sessionId] = useState(() => generateSessionId());

  const carbonProjects = useMemo(
    () =>
      products.filter(
        (product) =>
          product.productType === ImpactType.CARBON_OFFSETS ||
          product.productType === ImpactType.CARBON_REMOVAL
      ),
    [products]
  );

  const ukTreesProduct = useMemo(
    () => products.find((product) => product.slug === UK_TREES_SLUG),
    [products]
  );

  const globalTreesProduct = useMemo(
    () => products.find((product) => product.slug === GLOBAL_TREES_SLUG),
    [products]
  );

  const wildflowerProduct = useMemo(
    () => products.find((product) => product.slug === WILDFLOWER_SLUG),
    [products]
  );

  const peatlandProduct = useMemo(
    () => products.find((product) => product.slug === PEATLANDS_SLUG),
    [products]
  );

  const seagrassProduct = useMemo(
    () => products.find((product) => product.slug === SEAGRASS_SLUG),
    [products]
  );

  const handleClose = () => {
    if (
      onAnalyticsEvent &&
      (history.length > 0 || currentStep !== INITIAL_STEP)
    ) {
      let result: QuestionPathResult;
      const terminalNodeReached = isTerminalNode(currentStep);

      if (terminalNodeReached) {
        result = formatQuestionPath(
          history,
          currentStep,
          TERMINAL_NODES[currentStep]
        );
      } else {
        result =
          history.length > 0
            ? formatQuestionPath(history)
            : { questionPath: "{}", questionPathTruncatedAtStart: false };
      }

      onAnalyticsEvent("impactShopUnsureWhatToFundModalClosed", {
        questionPath: result.questionPath,
        questionPathTruncatedAtStart: result.questionPathTruncatedAtStart,
        terminalNodeReached,
        sessionId,
      });
    }

    onClose();
  };

  const handleOptionSelect = (next: string, selectedAnswer: string) => {
    setHistory((prev) => [
      ...prev,
      { step: currentStep, answer: selectedAnswer },
    ]);
    setCurrentStep(next);

    if (isTerminalNode(next) && onAnalyticsEvent) {
      const { questionPath, questionPathTruncatedAtStart } = formatQuestionPath(
        [...history, { step: currentStep, answer: selectedAnswer }],
        next,
        TERMINAL_NODES[next]
      );

      onAnalyticsEvent("impactShopUnsureWhatToFundRecommendation", {
        questionPath,
        questionPathTruncatedAtStart,
        terminalNodeReached: true,
        sessionId,
      });
    }
  };

  const handleBack = () => {
    if (history.length > 0) {
      const previousEntry = history[history.length - 1];
      const previousStep = previousEntry.step;

      if (onAnalyticsEvent) {
        const { questionPath, questionPathTruncatedAtStart } =
          formatQuestionPath(history);

        const terminalNodeReached =
          history.some((entry) => isTerminalNode(entry.step)) ||
          isTerminalNode(currentStep);

        onAnalyticsEvent("impactShopUnsureWhatToFundModalBackClicked", {
          questionPath,
          questionPathTruncatedAtStart,
          terminalNodeReached,
          sessionId,
        });
      }

      setHistory((prev) => prev.slice(0, -1));
      setCurrentStep(previousStep);
    }
  };

  const handleRecommendationClick = (productSlug: string) => {
    if (onAnalyticsEvent) {
      const { questionPath, questionPathTruncatedAtStart } = formatQuestionPath(
        history,
        currentStep,
        TERMINAL_NODES[currentStep]
      );

      onAnalyticsEvent("impactShopUnsureWhatToFundRecommendationClicked", {
        productSlug,
        questionPath,
        questionPathTruncatedAtStart,
        sessionId,
      });
    }

    handleClose();
  };

  const currentQuestion = questionTree[currentStep];
  const canGoBack = history.length > 0;

  const getProductPrice = (
    priceType: QuestionOption["dynamicPrice"]
  ): string | undefined => {
    if (!priceType) return undefined;

    let product: ImpactShopProductType | undefined;
    let pricePrefix = "";

    switch (priceType) {
      case "uk-trees":
        product = ukTreesProduct;
        break;
      case "global-trees":
        product = globalTreesProduct;
        break;
      case "land-habitat":
        product = wildflowerProduct;
        pricePrefix = "Starting at ";
        break;
      case "sea-habitat":
        product = seagrassProduct;
        pricePrefix = "Starting at ";
        break;
    }

    if (!product) return undefined;

    const priceData = product.prices.find((p) => p.currency === currency);
    if (!priceData) return undefined;

    const formattedPrice = penceToDisplayPrice(priceData.amount, currency);
    const unit = product.display.unitType;
    const unitText =
      unit === "tree" ? "/tree" : unit === "m2" ? " per sqm" : "";

    return `(${pricePrefix}${formattedPrice}${unitText})`;
  };

  const isGoalStep = currentStep === "goal";
  const isRadioOptionStep =
    currentStep === "emission-type" ||
    currentStep === "fixed-amount-per" ||
    currentStep === "habitat-fixed-amount-per";
  const isHorizontalCardStep =
    currentStep === "tree-budget" ||
    currentStep === "habitat-budget" ||
    currentStep === "measured";

  const renderQuestionView = () => {
    if (isGoalStep) {
      return (
        <ContentWrapper>
          <QuestionHeading size={5} as="h2">
            {currentQuestion.question}
          </QuestionHeading>
          <GoalCardsContainer>
            {currentQuestion.options.map((option) => (
              <GoalCard
                key={option.label}
                onClick={() => handleOptionSelect(option.next, option.label)}
              >
                <GoalCardBackground />
                <GoalCardContent>
                  {option.icon && <GoalCardIcon>{option.icon}</GoalCardIcon>}
                  <GoalCardTitle use="subtitle2">{option.label}</GoalCardTitle>
                </GoalCardContent>
              </GoalCard>
            ))}
          </GoalCardsContainer>
        </ContentWrapper>
      );
    }

    if (isRadioOptionStep) {
      return (
        <ContentWrapper>
          <QuestionHeading size={5} as="h2">
            {currentQuestion.question}
          </QuestionHeading>
          <RadioOptionsContainer>
            {currentQuestion.options.map((option) => (
              <RadioOption
                key={option.label}
                onClick={() => handleOptionSelect(option.next, option.label)}
              >
                {option.label}
              </RadioOption>
            ))}
          </RadioOptionsContainer>
        </ContentWrapper>
      );
    }

    if (isHorizontalCardStep) {
      return (
        <ContentWrapper>
          <QuestionHeading size={5} as="h2">
            {currentQuestion.question}
          </QuestionHeading>
          <OptionsContainer columns={2}>
            {currentQuestion.options.map((option) => (
              <HorizontalOptionCard
                key={option.label}
                onClick={() => handleOptionSelect(option.next, option.label)}
              >
                {option.icon && <IconWrapper>{option.icon}</IconWrapper>}
                <OptionTitle use="subtitle2">{option.label}</OptionTitle>
              </HorizontalOptionCard>
            ))}
          </OptionsContainer>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={5} as="h2">
          {currentQuestion.question}
        </QuestionHeading>
        <OptionsContainer columns={2}>
          {currentQuestion.options.map((option) => {
            const price = getProductPrice(option.dynamicPrice);

            return (
              <OptionCard
                key={option.label}
                onClick={() => handleOptionSelect(option.next, option.label)}
              >
                {option.icon && (
                  <OptionIconWrapper>{option.icon}</OptionIconWrapper>
                )}
                <OptionTitle use="subtitle2">{option.label}</OptionTitle>
                {price && <OptionPrice use="body2">{price}</OptionPrice>}
                {option.description && (
                  <OptionDescription use="body2">
                    {option.description}
                  </OptionDescription>
                )}
              </OptionCard>
            );
          })}
        </OptionsContainer>
      </ContentWrapper>
    );
  };

  const getProductPriceDisplay = (product: ImpactShopProductType): string => {
    const priceData = product.prices.find((p) => p.currency === currency);
    if (!priceData) return "";
    const formattedPrice = penceToDisplayPrice(priceData.amount, currency);
    const unit = product.display.unitType;
    if (unit === "tree") return `${formattedPrice}/tree`;
    if (unit === "m2") return `${formattedPrice}/sqm`;
    if (unit === "tonne CO2e") return `${formattedPrice}/tonne CO2e`;
    return formattedPrice;
  };

  const renderCarbonProjectsView = () => (
    <ContentWrapper>
      <QuestionHeading size={4} as="h2">
        Based on your answers here&apos;s some projects for you.
      </QuestionHeading>
      <ProjectsList>
        {carbonProjects.map((project) => (
          <Link
            key={project.slug}
            href={`/impact-shop/${project.slug}`}
            passHref
            legacyBehavior
          >
            <ProjectCard
              onClick={() => handleRecommendationClick(project.slug)}
            >
              {project.display.images?.[0] && (
                <ProjectImageWrapper>
                  <Image
                    src={project.display.images[0].url}
                    alt={project.display.title}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                </ProjectImageWrapper>
              )}
              <ProjectDetails>
                <ProjectTitle use="subtitle2">
                  {project.display.title}
                </ProjectTitle>
                <ProjectSnippet use="body2">
                  {project.display.snippet}
                </ProjectSnippet>
                <ProjectPrice use="body1">
                  {getProductPriceDisplay(project)}
                </ProjectPrice>
              </ProjectDetails>
            </ProjectCard>
          </Link>
        ))}
      </ProjectsList>
    </ContentWrapper>
  );

  const renderScheduleCallView = () => (
    <ContentWrapper>
      <ScheduleCallCard>
        <IconWrapper size={64} svgSize={56}>
          <HeadsetIcon />
        </IconWrapper>
        <ScheduleCallContent>
          <ScheduleCallTitle use="subtitle1">
            Speak to the team
          </ScheduleCallTitle>
          <ScheduleCallText use="body2">
            Our team can help you measure your emissions and find the right
            projects to fund.
          </ScheduleCallText>
        </ScheduleCallContent>
        <Link href="/page/schedule-a-call" passHref legacyBehavior>
          <PillButton
            onClick={() => handleRecommendationClick("schedule-call")}
          >
            Schedule a call
          </PillButton>
        </Link>
      </ScheduleCallCard>
    </ContentWrapper>
  );

  const renderTreeProductView = (
    product: ImpactShopProductType | undefined
  ) => {
    if (!product) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            Based on your answers here&apos;s a tree planting project for you.
          </QuestionHeading>
          <Typography use="body1">Product not available.</Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          Based on your answers here&apos;s a tree planting project for you.
        </QuestionHeading>
        <ProjectsList>
          <Link href={`/impact-shop/${product.slug}`} passHref legacyBehavior>
            <ProjectCard
              onClick={() => handleRecommendationClick(product.slug)}
            >
              {product.display.images?.[0] && (
                <ProjectImageWrapper>
                  <Image
                    src={product.display.images[0].url}
                    alt={product.display.title}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                </ProjectImageWrapper>
              )}
              <ProjectDetails>
                <ProjectTitle use="subtitle2">
                  {product.display.title}
                </ProjectTitle>
                <ProjectSnippet use="body2">
                  {product.display.snippet}
                </ProjectSnippet>
                <ProjectPrice use="body1">
                  {getProductPriceDisplay(product)}
                </ProjectPrice>
              </ProjectDetails>
            </ProjectCard>
          </Link>
        </ProjectsList>
        <Link
          href={`/impact-shop?impactType=${ImpactType.TREES}`}
          passHref
          legacyBehavior
        >
          <SeeOtherLink onClick={handleClose}>
            See other reforestation projects
          </SeeOtherLink>
        </Link>
      </ContentWrapper>
    );
  };

  const renderLandHabitatProductsView = () => {
    const landProducts = [wildflowerProduct, peatlandProduct].filter(
      (p): p is ImpactShopProductType => p !== undefined
    );

    if (landProducts.length === 0) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            Based on your answers here&apos;s some projects for you.
          </QuestionHeading>
          <Typography use="body1">
            No land habitat products available.
          </Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          Based on your answers here&apos;s some projects for you.
        </QuestionHeading>
        <ProjectsList>
          {landProducts.map((product) => (
            <Link
              key={product.slug}
              href={`/impact-shop/${product.slug}`}
              passHref
              legacyBehavior
            >
              <ProjectCard
                onClick={() => handleRecommendationClick(product.slug)}
              >
                {product.display.images?.[0] && (
                  <ProjectImageWrapper>
                    <Image
                      src={product.display.images[0].url}
                      alt={product.display.title}
                      fill
                      style={{ objectFit: "cover" }}
                    />
                  </ProjectImageWrapper>
                )}
                <ProjectDetails>
                  <ProjectTitle use="subtitle2">
                    {product.display.title}
                  </ProjectTitle>
                  <ProjectSnippet use="body2">
                    {product.display.snippet}
                  </ProjectSnippet>
                  <ProjectPrice use="body1">
                    {getProductPriceDisplay(product)}
                  </ProjectPrice>
                </ProjectDetails>
              </ProjectCard>
            </Link>
          ))}
        </ProjectsList>
        <Link
          href={`/impact-shop?impactType=${ImpactType.HABITAT_RESTORATION}`}
          passHref
          legacyBehavior
        >
          <SeeOtherLink onClick={handleClose}>
            See other habitat restoration projects
          </SeeOtherLink>
        </Link>
      </ContentWrapper>
    );
  };

  const renderSeaHabitatProductView = () => {
    if (!seagrassProduct) {
      return (
        <ContentWrapper>
          <QuestionHeading size={4} as="h2">
            Based on your answers here&apos;s a habitat restoration project for
            you.
          </QuestionHeading>
          <Typography use="body1">Product not available.</Typography>
        </ContentWrapper>
      );
    }

    return (
      <ContentWrapper>
        <QuestionHeading size={4} as="h2">
          Based on your answers here&apos;s a habitat restoration project for
          you.
        </QuestionHeading>
        <ProjectsList>
          <Link
            href={`/impact-shop/${seagrassProduct.slug}`}
            passHref
            legacyBehavior
          >
            <ProjectCard
              onClick={() => handleRecommendationClick(seagrassProduct.slug)}
            >
              {seagrassProduct.display.images?.[0] && (
                <ProjectImageWrapper>
                  <Image
                    src={seagrassProduct.display.images[0].url}
                    alt={seagrassProduct.display.title}
                    fill
                    style={{ objectFit: "cover" }}
                  />
                </ProjectImageWrapper>
              )}
              <ProjectDetails>
                <ProjectTitle use="subtitle2">
                  {seagrassProduct.display.title}
                </ProjectTitle>
                <ProjectSnippet use="body2">
                  {seagrassProduct.display.snippet}
                </ProjectSnippet>
                <ProjectPrice use="body1">
                  {getProductPriceDisplay(seagrassProduct)}
                </ProjectPrice>
              </ProjectDetails>
            </ProjectCard>
          </Link>
        </ProjectsList>
        <Link
          href={`/impact-shop?impactType=${ImpactType.HABITAT_RESTORATION}`}
          passHref
          legacyBehavior
        >
          <SeeOtherLink onClick={handleClose}>
            See other habitat restoration projects
          </SeeOtherLink>
        </Link>
      </ContentWrapper>
    );
  };

  const renderContent = () => {
    if (currentStep === "carbon-projects") {
      return renderCarbonProjectsView();
    }
    if (currentStep === "schedule-call") {
      return renderScheduleCallView();
    }
    if (currentStep === "uk-trees") {
      return renderTreeProductView(ukTreesProduct);
    }
    if (currentStep === "global-trees") {
      return renderTreeProductView(globalTreesProduct);
    }
    if (currentStep === "land-habitat-products") {
      return renderLandHabitatProductsView();
    }
    if (currentStep === "seagrass") {
      return renderSeaHabitatProductView();
    }
    return renderQuestionView();
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={handleClose}
      dataTestId="unsure-what-to-fund-modal"
      headerLeft={
        canGoBack ? (
          <BackButton
            onClick={handleBack}
            aria-label="Go back to previous question"
            data-testid="unsure-what-to-fund-back-button"
          >
            ← Back
          </BackButton>
        ) : undefined
      }
    >
      {renderContent()}
    </DialogModal>
  );
};
